/**
 * Authentication Module for eureka-ai-assistant
 *
 * USAGE:
 * Host applications MUST call initAuth() before using any auth features.
 *
 * Mode 'keycloak' - Standard Keycloak OIDC flow:
 *   initAuth({ mode: 'keycloak', keycloak: { url, realm, clientId } });
 *
 * Mode 'external' - Host app provides tokens:
 *   initAuth({ mode: 'external', tokenProvider: async () => keycloak.token });
 *
 * Mode 'none' - No authentication (local dev):
 *   initAuth({ mode: 'none' });
 */

import Keycloak from "keycloak-js";
import type KeycloakInstance from "keycloak-js";

// =============================================================================
// Types
// =============================================================================

export type AuthMode = 'keycloak' | 'external' | 'none';

export interface KeycloakAuthConfig {
  url: string;
  realm: string;
  clientId: string;
}

export interface AuthConfig {
  mode: AuthMode;
  /** Required when mode is 'keycloak' */
  keycloak?: KeycloakAuthConfig;
  /** Required when mode is 'external' */
  tokenProvider?: () => Promise<string | null> | string | null;
  /** Optional: enable dev login API even in external mode */
  enableDevLogin?: boolean;
}

export interface KeycloakTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
}

export interface DevUserInfo {
  sub: string;
  email: string;
  name: string;
  preferred_username: string;
  given_name?: string;
  family_name?: string;
}

// =============================================================================
// State
// =============================================================================

let _authConfig: AuthConfig | null = null;
let _keycloakInstance: KeycloakInstance | null = null;
let _initialized = false;

// Storage keys for dev login tokens
const DEV_ACCESS_TOKEN_KEY = "dev_access_token";
const DEV_REFRESH_TOKEN_KEY = "dev_refresh_token";
const DEV_TOKEN_EXPIRY_KEY = "dev_token_expiry";
const DEV_USER_INFO_KEY = "dev_user_info";

// =============================================================================
// Initialization
// =============================================================================

/**
 * Initialize the authentication module.
 * Must be called before any auth operations.
 *
 * @example
 * // Keycloak mode - library handles OIDC flow
 * initAuth({
 *   mode: 'keycloak',
 *   keycloak: {
 *     url: 'https://keycloak.example.com',
 *     realm: 'myrealm',
 *     clientId: 'my-app'
 *   }
 * });
 *
 * @example
 * // External mode - host app provides tokens
 * initAuth({
 *   mode: 'external',
 *   tokenProvider: async () => {
 *     await keycloak.updateToken(30);
 *     return keycloak.token;
 *   }
 * });
 *
 * @example
 * // No auth mode - for local development
 * initAuth({ mode: 'none' });
 */
export function initAuth(config: AuthConfig): void {
  // Validate config
  if (config.mode === 'keycloak') {
    if (!config.keycloak) {
      throw new Error('initAuth: keycloak config required when mode is "keycloak"');
    }
    if (!config.keycloak.url || !config.keycloak.realm || !config.keycloak.clientId) {
      throw new Error('initAuth: keycloak.url, realm, and clientId are required');
    }
  }

  if (config.mode === 'external') {
    if (!config.tokenProvider) {
      throw new Error('initAuth: tokenProvider required when mode is "external"');
    }
  }

  _authConfig = config;
  _keycloakInstance = null; // Reset any existing instance
  _initialized = true;

  console.log(`[Auth] Initialized with mode: ${config.mode}`);
}

/**
 * Check if auth has been initialized
 */
export function isAuthInitialized(): boolean {
  return _initialized;
}

/**
 * Get current auth mode
 */
export function getAuthMode(): AuthMode | null {
  return _authConfig?.mode ?? null;
}

/**
 * Get the current auth config (for debugging)
 */
export function getAuthConfig(): AuthConfig | null {
  return _authConfig;
}

// =============================================================================
// Keycloak Instance Management
// =============================================================================

/**
 * Get the Keycloak instance (only available in 'keycloak' mode)
 * Creates instance lazily in browser only
 */
export function getKeycloakInstance(): KeycloakInstance | null {
  if (!_authConfig || _authConfig.mode !== 'keycloak') {
    return null;
  }

  // Only create in browser
  if (typeof window === "undefined") {
    return null;
  }

  // Create instance lazily
  if (!_keycloakInstance && _authConfig.keycloak) {
    _keycloakInstance = new Keycloak({
      url: _authConfig.keycloak.url,
      realm: _authConfig.keycloak.realm,
      clientId: _authConfig.keycloak.clientId,
    });
  }

  return _keycloakInstance;
}

// =============================================================================
// Token Management
// =============================================================================

/**
 * Get the current auth token.
 * Works across all auth modes.
 */
export async function getToken(): Promise<string | null> {
  if (!_authConfig) {
    console.warn('[Auth] getToken called before initAuth()');
    return null;
  }

  switch (_authConfig.mode) {
    case 'external':
      return getExternalToken();

    case 'keycloak':
      return getKeycloakToken();

    case 'none':
      // In 'none' mode, try dev token first, then env var
      if (typeof window !== "undefined") {
        const devToken = await getDevToken();
        if (devToken) return devToken;
      }
      return process.env.NEXT_PUBLIC_DEV_TOKEN || null;

    default:
      return null;
  }
}

/**
 * Get token from external provider
 */
async function getExternalToken(): Promise<string | null> {
  if (!_authConfig?.tokenProvider) {
    return null;
  }

  try {
    const result = _authConfig.tokenProvider();
    if (result instanceof Promise) {
      return await result;
    }
    return result;
  } catch (error) {
    console.error('[Auth] External token provider error:', error);
    return null;
  }
}

/**
 * Get token from Keycloak instance
 */
async function getKeycloakToken(): Promise<string | null> {
  const kc = getKeycloakInstance();
  if (!kc) {
    return null;
  }

  try {
    // Refresh if token expires within 30 seconds
    if (kc.isTokenExpired(30)) {
      console.log("[Auth] Token expiring soon, refreshing...");
      await kc.updateToken(30);
    }
    return kc.token || null;
  } catch (error) {
    console.error("[Auth] Failed to get/refresh token:", error);
    // Redirect to login if refresh fails
    kc.login();
    return null;
  }
}

/**
 * Force refresh the Keycloak token (only works in 'keycloak' mode)
 */
export async function forceRefreshToken(): Promise<string | null> {
  if (!_authConfig || _authConfig.mode !== 'keycloak') {
    return null;
  }

  const kc = getKeycloakInstance();
  if (!kc) return null;

  try {
    await kc.updateToken(-1); // -1 forces immediate refresh
    return kc.token || null;
  } catch (error) {
    console.error("[Auth] Failed to refresh token:", error);
    return null;
  }
}

// =============================================================================
// Logout
// =============================================================================

/**
 * Logout the current user
 */
export function logout(): void {
  if (!_authConfig) {
    console.warn('[Auth] logout called before initAuth()');
    return;
  }

  switch (_authConfig.mode) {
    case 'keycloak': {
      const kc = getKeycloakInstance();
      if (kc) kc.logout();
      break;
    }
    case 'external':
    case 'none':
      // Clear dev tokens if any
      clearDevTokens();
      break;
  }
}

// =============================================================================
// Dev Login (Password Grant)
// =============================================================================

/**
 * Check if dev login is available
 */
export function isDevLoginEnabled(): boolean {
  if (!_authConfig) return false;

  // Dev login is available in 'none' mode or when explicitly enabled
  return _authConfig.mode === 'none' || _authConfig.enableDevLogin === true;
}

/**
 * Direct login using Keycloak password grant
 * Only works when dev login is enabled
 */
export async function directLogin(
  username: string,
  password: string
): Promise<{ success: boolean; error?: string; userInfo?: DevUserInfo }> {
  if (!isDevLoginEnabled()) {
    return { success: false, error: "Dev login not enabled" };
  }

  try {
    console.log("[Auth] Dev login for:", username);

    // Use local proxy to avoid CORS issues
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json();

    if (!data.success) {
      console.error("[Auth] Dev login failed:", data.error);
      return { success: false, error: data.error || "Authentication failed" };
    }

    // Store tokens
    storeDevTokens(data.tokenData);

    // Store user info
    if (data.userInfo) {
      localStorage.setItem(DEV_USER_INFO_KEY, JSON.stringify(data.userInfo));
    }

    console.log("[Auth] Dev login successful for:", username);
    return { success: true, userInfo: data.userInfo || undefined };
  } catch (error) {
    console.error("[Auth] Dev login error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}

/**
 * Refresh the dev access token using refresh token
 */
export async function refreshDevToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem(DEV_REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    const data = await response.json();

    if (!data.success) {
      console.error("[Auth] Dev token refresh failed");
      clearDevTokens();
      return false;
    }

    storeDevTokens(data.tokenData);
    return true;
  } catch (error) {
    console.error("[Auth] Dev token refresh error:", error);
    clearDevTokens();
    return false;
  }
}

function storeDevTokens(tokenData: KeycloakTokenResponse): void {
  const expiryTime = Date.now() + tokenData.expires_in * 1000;
  localStorage.setItem(DEV_ACCESS_TOKEN_KEY, tokenData.access_token);
  localStorage.setItem(DEV_REFRESH_TOKEN_KEY, tokenData.refresh_token);
  localStorage.setItem(DEV_TOKEN_EXPIRY_KEY, expiryTime.toString());
}

export function clearDevTokens(): void {
  localStorage.removeItem(DEV_ACCESS_TOKEN_KEY);
  localStorage.removeItem(DEV_REFRESH_TOKEN_KEY);
  localStorage.removeItem(DEV_TOKEN_EXPIRY_KEY);
  localStorage.removeItem(DEV_USER_INFO_KEY);
  localStorage.removeItem("dev_auth_token"); // Legacy key
}

/**
 * Get the current dev access token, refreshing if needed
 */
export async function getDevToken(): Promise<string | null> {
  const accessToken = localStorage.getItem(DEV_ACCESS_TOKEN_KEY);
  const expiryStr = localStorage.getItem(DEV_TOKEN_EXPIRY_KEY);

  if (!accessToken) {
    return null;
  }

  // Check if token is expired or expiring soon (within 30 seconds)
  if (expiryStr) {
    const expiry = parseInt(expiryStr, 10);
    const now = Date.now();

    if (now >= expiry - 30000) {
      console.log("[Auth] Dev token expiring, attempting refresh...");
      const refreshed = await refreshDevToken();
      if (refreshed) {
        return localStorage.getItem(DEV_ACCESS_TOKEN_KEY);
      }
      return null;
    }
  }

  return accessToken;
}

/**
 * Get stored dev user info
 */
export function getDevUserInfo(): DevUserInfo | null {
  if (typeof window === "undefined") return null;

  const userInfoStr = localStorage.getItem(DEV_USER_INFO_KEY);
  if (!userInfoStr) return null;

  try {
    return JSON.parse(userInfoStr);
  } catch {
    return null;
  }
}

/**
 * Check if user is logged in via dev login
 */
export function isDevLoggedIn(): boolean {
  if (typeof window === "undefined") return false;
  return !!localStorage.getItem(DEV_ACCESS_TOKEN_KEY);
}

// =============================================================================
// Backward Compatibility - DEPRECATED
// =============================================================================

/**
 * @deprecated Use initAuth({ mode: 'external', tokenProvider }) instead
 */
export function setExternalTokenProvider(
  provider: (() => Promise<string | null> | string | null) | null
): void {
  console.warn(
    '[Auth] setExternalTokenProvider is deprecated. Use initAuth({ mode: "external", tokenProvider }) instead.'
  );

  if (provider) {
    // If not initialized, initialize with external mode
    if (!_initialized) {
      initAuth({ mode: 'external', tokenProvider: provider });
    } else if (_authConfig) {
      // Update the token provider
      _authConfig.tokenProvider = provider;
    }
  }
}

/**
 * @deprecated No longer needed - auth mode is set via initAuth()
 */
export const keycloakDisabled: boolean = false;

/**
 * @deprecated Use getKeycloakInstance() instead
 */
export const keycloak: KeycloakInstance | null = null;
